/**
 * <VideoTile> — un participant du live, là où passerait sa vidéo (molecule).
 *
 * Le design system ne dépend d'aucun moteur vidéo : l'écran pose la vraie piste (LiveKit) dans la
 * tuile par deux fentes, `video` (sa caméra) et `screenVideo` (l'écran qu'il partage), qui
 * remplissent la tuile à la place de la photo ou de l'image. Sans elles, la tuile reste la surface
 * de maquette d'avant ; avec ou sans, elle dit tout ce qu'elle montre autour de la vidéo :
 *  - caméra ouverte : la photo du participant remplit la tuile, à la place de son image ;
 *  - caméra coupée : son visage (`<Avatar>`, la capsule pour le tuteur) au milieu du fond de nuit ;
 *  - l'écran que le tuteur partage (`screenUri`) : l'image entière, sur le même fond ;
 *  - son nom en bas à gauche (« Toi » sur la mienne), le micro coupé en haut à droite, la main
 *    levée en haut à gauche, sur l'aplat jaune d'un repère (ce n'est pas un bouton).
 *
 * Cinq tailles : `stage` remplit son conteneur (la scène) ; `thumb` est une vignette posée en
 * coin de la scène (ma caméra, et celle du tuteur quand il partage son écran) ; `tile` remplit son
 * conteneur comme `stage`, mais avec les repères d'une vignette (la pellicule du masteur, une case
 * de quelques dizaines de pixels de haut) ; `mini` est la
 * mini-visio du tuteur dans la bascule de l'en-tête, sans nom ni repère : elle ne sert qu'à le
 * voir ; `round` est ma caméra dans la barre du live, un rond de la taille d'un bouton, sans nom ni
 * repère non plus.
 *
 * Les autres élèves d'un cours partagé n'ont jamais de tuile (Wajih, 17/10) : ni leur caméra ni
 * leur prénom ne se montrent, la scène pose leurs visages en pile (`<AvatarStack>`).
 *
 * Le fond est la surface nuit (`surface-night`) dans les deux thèmes : une vidéo n'a pas de clair.
 */
import React from "react"
import { Image, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Avatar } from "../../atoms/Avatar"
import { Row } from "../../atoms/containers/Row"
import { Icon } from "../../atoms/icons/Icon"
import { IconBubble } from "../../atoms/icons/IconBubble"
import { Text } from "../../atoms/typography/Text"
import { type UILiveParticipant } from "../../../ports/primary/ui/types/studentLive"
import { MARK_INSET, STAGE_MARK, STAGE_NAME_MAX_RATIO, VIDEO_THUMB } from "./videoTileGeometry"

// Un participant du live : sa forme vit dans `ports/primary/ui/types` (socle du live).
export type { UILiveParticipant }

export type VideoTileSize = "stage" | "thumb" | "tile" | "mini" | "round"

export type VideoTileProps = {
    participant: UILiveParticipant
    /** Défaut `thumb`. */
    size?: VideoTileSize
    /** `round` seulement : son diamètre, celui des boutons qu'il côtoie. Défaut 48. */
    diameter?: number
    /** Ma tuile : elle s'intitule « Toi ». Défaut `false`. */
    me?: boolean
    /** L'écran que le participant partage, à la place de sa caméra : son image. */
    screenUri?: string
    /**
     * La vraie piste de sa caméra (l'écran la rend, `VideoView` de LiveKit) : elle remplit la tuile à
     * la place de sa photo, caméra ouverte et participant présent. Absente : la photo, comme avant.
     */
    video?: React.ReactNode
    /**
     * La vraie piste de l'écran qu'il partage : elle remplit la tuile, comme `screenUri`, qu'elle
     * remplace. Absente : `screenUri`, ou sa caméra.
     */
    screenVideo?: React.ReactNode
    /** Le participant est attendu, pas encore là : son visage estompé. Défaut `false`. */
    absent?: boolean
    testID?: string
}

/** La mini-visio du tuteur, dans la bascule de l'en-tête du live (téléphone). */
const VIDEO_MINI = { width: 48, height: 36 } as const

const AVATAR_SIZE: Record<VideoTileSize, number> = { stage: 96, thumb: 44, tile: 32, mini: 24, round: 28 }

const TEXTS = {
    me: "Toi",
    screen: (name: string) => `Écran de ${name}`,
    micOff: "Micro coupé",
    hand: "Main levée",
}

export function VideoTile(props: VideoTileProps) {
    const {
        participant,
        size = "thumb",
        diameter = 48,
        me = false,
        screenUri,
        video,
        screenVideo,
        absent = false,
        testID,
    } = props
    const { name, avatarUrl, tutor = false, cameraOff = false, micOff = false, handRaised = false } = participant

    const showsScreen = screenUri != null || screenVideo != null
    const showsCamera = !showsScreen && !cameraOff && !absent
    const showsVideo = showsCamera && video != null
    const showsPhoto = showsCamera && !showsVideo && avatarUrl != null
    const label = showsScreen ? TEXTS.screen(name) : me ? TEXTS.me : name
    const marks = size === "stage" || size === "thumb" || size === "tile"
    const showsMicOff = marks && micOff && !showsScreen
    const showsHand = marks && handRaised
    // Une seule lecture : iOS ignore le nom d'une vue qui n'est pas `accessible`, et les repères en coin
    // n'en sont pas. La tuile dit qui, puis ce que ses repères montrent.
    const spoken = [label, showsMicOff ? TEXTS.micOff : null, showsHand ? TEXTS.hand : null]
        .filter((part) => part != null)
        .join(", ")

    return (
        <View
            style={styles.tile(size, diameter)}
            testID={testID}
            accessible
            accessibilityRole="image"
            accessibilityLabel={spoken}
        >
            {showsScreen ? (
                screenVideo != null ? (
                    <View style={styles.fill}>{screenVideo}</View>
                ) : (
                    <Image source={{ uri: screenUri }} style={styles.fill} resizeMode="contain" />
                )
            ) : showsVideo ? (
                <View style={styles.fill}>{video}</View>
            ) : showsPhoto ? (
                <Image source={{ uri: avatarUrl }} style={styles.fill} resizeMode="cover" />
            ) : (
                <View style={styles.center}>
                    <Avatar
                        uri={avatarUrl}
                        name={name}
                        size={AVATAR_SIZE[size]}
                        variant={tutor ? "tutor" : "default"}
                        dimmed={absent}
                    />
                </View>
            )}

            {marks && (
                <View style={styles.name(size)} pointerEvents="none">
                    <Row gap="1" align="center">
                        {cameraOff && !showsScreen && <Icon name="video-slash" size={12} color="white" />}
                        <Text
                            size={size === "stage" ? "text-md" : "text-xs"}
                            weight="semibold"
                            color="white"
                            numberOfLines={1}
                            style={styles.nameText}
                        >
                            {label}
                        </Text>
                    </Row>
                </View>
            )}

            {showsMicOff && (
                <View style={styles.corner("right")}>
                    <IconBubble
                        iconName="microphone-slash"
                        size={size === "stage" ? STAGE_MARK : 24}
                        color="backdrop"
                        iconColor="white"
                    />
                </View>
            )}

            {showsHand && (
                <View style={styles.corner("left")}>
                    <IconBubble
                        iconName="hand-paper"
                        size={size === "stage" ? STAGE_MARK : 24}
                        color="accent"
                        iconColor="text-on-accent"
                    />
                </View>
            )}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    tile: (size: VideoTileSize, diameter: number) => ({
        overflow: "hidden" as const,
        backgroundColor: theme.colors["surface-night"],
        ...(size === "stage"
            ? { flex: 1, minHeight: 0, alignSelf: "stretch" as const, borderRadius: theme.radius.lg }
            : size === "thumb"
              ? { width: VIDEO_THUMB.width, height: VIDEO_THUMB.height, borderRadius: theme.radius.md }
              : size === "tile"
                ? { flex: 1, minHeight: 0, alignSelf: "stretch" as const, borderRadius: theme.radius.md }
                : size === "mini"
                  ? { width: VIDEO_MINI.width, height: VIDEO_MINI.height, borderRadius: theme.radius.sm }
                  : { width: diameter, height: diameter, borderRadius: diameter / 2 }),
    }),
    fill: { position: "absolute", top: 0, right: 0, bottom: 0, left: 0, width: "100%", height: "100%" },
    center: { flex: 1, alignItems: "center", justifyContent: "center" },
    name: (size: VideoTileSize) => ({
        position: "absolute" as const,
        left: theme.spacing[size === "stage" ? "3" : "1"],
        bottom: theme.spacing[size === "stage" ? "3" : "1"],
        // Sur la scène, le coin bas droit reste à ma vignette (`stageLayout`).
        maxWidth: size === "stage" ? (`${STAGE_NAME_MAX_RATIO * 100}%` as const) : ("90%" as const),
        backgroundColor: theme.colors.backdrop,
        borderRadius: theme.radius.sm,
        paddingHorizontal: theme.spacing[size === "stage" ? "2" : "1"],
        paddingVertical: theme.spacing["1"],
    }),
    nameText: { flexShrink: 1 },
    corner: (side: "left" | "right") => ({
        position: "absolute" as const,
        top: MARK_INSET,
        [side]: MARK_INSET,
    }),
}))
